/**
 * Privacy policy.
 *
 * Reachable without signing in, because Google's verification review reads it and because
 * someone deciding whether to connect a calendar should be able to read it before they do.
 *
 * Written to describe what the software actually does. Every claim here is checkable against
 * the code, and it says plainly where data leaves the account — a policy that overstates
 * containment is worse than none, because it is the thing people rely on when deciding.
 */
export function PrivacyPolicy() {
  return (
    <div className="shell">
      <header className="top">
        <h1>unschool.works</h1>
        <span className="spacer" />
        <a href="/">Back to the app</a>
      </header>

      <div className="panel prose">
        <h2>Privacy policy</h2>
        <p className="muted">Last updated 20 August 2026</p>

        <p>
          unschool.works keeps a record of homeschool instruction so a family can show how
          many hours they taught, in which subjects, if a state asks. It is operated by an
          individual, not a company, and is invite-only.
        </p>

        <h3>What is stored</h3>
        <ul>
          <li>
            <strong>Who you are.</strong> The email address and name on the Google account you
            sign in with. No password is ever seen or stored — Google handles sign-in.
          </li>
          <li>
            <strong>Your students.</strong> The names, and optionally the grade and birth date,
            of the children whose instruction is being recorded.
          </li>
          <li>
            <strong>The log.</strong> Dates, times, subjects and short descriptions of what was
            taught, and which students each entry counts for.
          </li>
          <li>
            <strong>Recordings, if you use them.</strong> Audio you dictate, or that a
            recording device uploads, together with the transcript produced from it.
          </li>
          <li>
            <strong>Calendar entries, if you connect a calendar.</strong> The title, notes,
            location and times of entries in the window being checked.
          </li>
        </ul>

        <h3>Google Calendar</h3>
        <p>
          Connecting a calendar is optional, separate from signing in, and can be undone at any
          time in Settings or from your Google account's third-party access page. Access is
          requested read-only (<code>calendar.readonly</code>): this app cannot create, change
          or delete anything in your calendar, and does not try to.
        </p>
        <p>
          Entries are read only to work out which ones look like instruction. What is kept
          afterwards is the resulting log entry — its date, time, subject and description —
          plus the identifier of the calendar entry it came from, so the same one is not
          imported twice. Entries judged not to be instruction are recorded only as an
          identifier and title, so they are not re-examined on every check.
        </p>
        <p>
          Calendar data is never sold, never used for advertising, and never used to train any
          model. Disconnecting stops all further reading; log entries already created stay,
          because they are part of your record.
        </p>

        <h3>Where data goes</h3>
        <p>Records are held in Amazon Web Services in the United States. Three other services see data, each for one purpose:</p>
        <ul>
          <li>
            <strong>Google</strong> — sign-in, and reading your calendar if you connect one.
          </li>
          <li>
            <strong>Anthropic</strong> — the text of an instruction you type or dictate, a
            transcript, or a calendar entry's title and notes, sent so it can be classified and
            described. Anthropic does not train on data submitted through its API.
          </li>
          <li>
            <strong>A speech-to-text provider</strong> (currently Google Gemini) — audio you
            record, sent to be transcribed.
          </li>
        </ul>
        <p>
          Nothing is sold, and nothing is shared with anyone else. There is no advertising and
          no analytics.
        </p>

        <h3>Children</h3>
        <p>
          This service is used by parents, not by children. The information recorded about a
          child is what a parent chooses to record — a name, a grade, and a description of
          lessons — and, where a family uses recording, audio of lessons that may include
          children speaking. That audio is used to produce a transcript and nothing else. It is
          reachable only by the members of that family's own account.
        </p>

        <h3>Keeping and deleting</h3>
        <p>
          Records are kept for as long as an account exists, because an instruction log is
          useful years later. Anything can be deleted from within the app: an entry, a student,
          or an entire account. Deleting a student who was recorded by mistake removes them and
          their entries from the log and from every export; the underlying rows are held in an
          archive for ninety days so an accidental deletion can be undone, then removed.
        </p>
        <p>
          To have an entire account and everything in it deleted, email the address below.
        </p>

        <h3>Access</h3>
        <p>
          Only members of your own account can see your records. Accounts are separated at the
          storage layer, not by a filter. The person operating the service can see which
          accounts exist, how many students they have, and what they cost to run, and
          deliberately cannot see anyone's log entries from the operator console.
        </p>

        <h3>Contact</h3>
        <p>
          Questions, corrections, or deletion requests:{' '}
          <a href="mailto:bpodgursky@gmail.com">bpodgursky@gmail.com</a>
        </p>
      </div>
    </div>
  );
}
